import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import withErrorHandling from "../HOC/ErrorHandling";
import { useSharedProps } from "../HOC/SharedProps";

import {
  Menu as MenuIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  NotificationsOutlined,
  KeyboardArrowDown,
  Logout,
  Settings,
  Person,
  DarkMode,
  LightMode,
  RestartAlt,
} from "@mui/icons-material";

import {
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Avatar,
  Popper,
  ClickAwayListener,
  Paper,
  IconButton,
  Box,
  CircularProgress,
  Badge,
  Typography,
  Chip,
  Divider,
} from "@mui/material";

import t from "../helpers/en";
import Modal from "./Modal/Modal";
import { styleEllipsis } from "../GeneralStyles";
import { hasPermission } from "../helpers/selectors";
import TrialChip from "./TrialChip";
import { API_URL } from "../helpers/constants";
import { getToken } from "../helpers/tokenStorage";
import { safeLocalStorageClear as clearGettingStartedDismissal } from "./GettingStartedModal";

const popperVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.15, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.96,
    transition: { duration: 0.1 },
  },
};

const ProjectSelector = ({ onOpen, modalData, currentGroup, isDarkMode }) => {
  const colors = {
    label: isDarkMode ? "#94a3b8" : "#64748b",
    value: isDarkMode ? "#e2e8f0" : "#1e293b",
    icon: isDarkMode ? "#64748b" : "#94a3b8",
    hoverBg: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
  };

  return (
    <Box
      component={motion.button}
      whileHover={{ background: colors.hoverBg }}
      onClick={onOpen}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: "6px",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
    >
      {modalData?.isLoading ? (
        <CircularProgress size={14} sx={{ color: colors.label }} />
      ) : (
        <>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: colors.label,
              fontWeight: 500,
            }}
          >
            Project:
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: colors.value,
              fontWeight: 600,
              maxWidth: "140px",
              ...styleEllipsis,
            }}
          >
            {currentGroup?.name ?? t.noProject}
          </Typography>
          <KeyboardArrowDown sx={{ fontSize: "1rem", color: colors.icon }} />
        </>
      )}
    </Box>
  );
};

const AppBarMain = (props) => {
  const {
    open: drawerOpen,
    onLogout,
    currentUser,
    viewport,
    onToggleDrawer,
    onOpen,
    modalData,
    projectToDelete,
    handleConfirmDelete,
    handleCancelDelete,
    confirmDelete,
    onMenuOpen,
    notifications,
    handleThemeToggle,
    isDarkMode,
  } = props;

  const isSmallScreen = viewport < 1200;
  const location = useLocation();
  const navigate = useNavigate();
  const { onSetGroup, currentGroup } = useSharedProps();

  const [anchorAvatarEl, setAnchorAvatarEl] = useState(null);

  const onOpenAvatarPopper = (evt) => {
    setAnchorAvatarEl(anchorAvatarEl ? null : evt.currentTarget);
  };

  const onCloseAvatarPopper = () => {
    setAnchorAvatarEl(null);
  };

  const openAvatarPopup = Boolean(anchorAvatarEl);
  const id = openAvatarPopup ? "AVATAR_POPPER" : undefined;

  const getInitials = (name) => {
    const parts = name?.split(" ") || [];
    return parts.length >= 2
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : name?.[0]?.toUpperCase() || "?";
  };

  const onMove2Page = (cb) => {
    cb && cb();
    navigate("/account");
  };

  const unreadCount = notifications?.filter((it) => !it.isRead).length || 0;

  // SCK-607 — reset the per-user "getting-started modal dismissed"
  // preference. Clears both the local fast-path cache AND the coord-
  // side flag; the next mount will refetch invitation-kind + open
  // the modal again. Fire-and-forget on the server call — the local
  // clear alone is enough to make the modal reappear on next mount;
  // the server call keeps the reset consistent across devices.
  const onResetGettingStarted = () => {
    clearGettingStartedDismissal();
    const token = getToken();
    if (token) {
      fetch(`${API_URL}/me/preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        // Explicit null tells coord to REMOVE the key (see
        // MePreferencesController.putPreferences).
        body: JSON.stringify({ gettingStartedModal: null }),
      }).catch(() => { /* local clear already applied */ });
    }
    onCloseAvatarPopper();
  };

  const menuItems = [
    ...(hasPermission(currentUser, ["SYSTEM"])
      ? [{
          icon: <Settings sx={{ fontSize: "1.1rem" }} />,
          name: t.accountSettings,
          onClick: () => onMove2Page(onCloseAvatarPopper),
        }]
      : [{
          icon: <Person sx={{ fontSize: "1.1rem" }} />,
          name: t.accountManagement,
          onClick: () => onMove2Page(onCloseAvatarPopper),
        }]),
    {
      icon: <RestartAlt sx={{ fontSize: "1.1rem" }} />,
      name: "Show getting-started tips again",
      onClick: onResetGettingStarted,
      dataTestId: "reset-getting-started",
    },
    {
      icon: <Logout sx={{ fontSize: "1.1rem" }} />,
      name: t.logout,
      onClick: () => onLogout(() => {
        onSetGroup();
        navigate("/login", { state: { from: location } });
        onCloseAvatarPopper();
      }),
      danger: true,
    },
  ];

  // Theme-responsive colors for AppBar
  const appBarColors = {
    bg: isDarkMode
      ? "linear-gradient(90deg, #0f172a 0%, #1e293b 100%)"
      : "linear-gradient(90deg, #ffffff 0%, #f8fafc 100%)",
    border: isDarkMode ? "#334155" : "#e2e8f0",
    hoverBg: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
    iconColor: isDarkMode ? "#e2e8f0" : "#475569",
    textPrimary: isDarkMode ? "#e2e8f0" : "#1e293b",
    textSecondary: isDarkMode ? "#94a3b8" : "#64748b",
    textMuted: isDarkMode ? "#64748b" : "#94a3b8",
    chipBg: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
  };

  return (
    <>
      {/* App Bar */}
      <Box
        component={motion.header}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          position: "sticky",
          width: "100%",
          background: appBarColors.bg,
          display: "flex",
          alignItems: "center",
          px: 2,
          // Grow (don't just pad) by the top inset so content stays
          // vertically centered in the original 60px band — this is a
          // no-op (env() = 0) unless viewport-fit=cover is set and the
          // device reports a real inset, e.g. iPad Pro landscape.
          height: "calc(60px + env(safe-area-inset-top))",
          pt: "env(safe-area-inset-top)",
          top: 0,
          zIndex: 100,
          borderBottom: `1px solid ${appBarColors.border}`,
          transition: "background 0.3s ease, border-color 0.3s ease",
        }}
      >
        {/* Left Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Logo */}
          {!isSmallScreen && (
            <Link to="/" style={{ display: "flex", alignItems: "center" }}>
              <img
                style={{ height: "32px" }}
                src={process.env.PUBLIC_URL + "/logo96.png"}
                alt="Clue2App"
              />
            </Link>
          )}

          {/* Menu Toggle */}
          <IconButton
            onClick={onToggleDrawer}
            size="small"
            sx={{
              background: appBarColors.hoverBg,
              borderRadius: "6px",
              width: 30,
              height: 30,
              "&:hover": { background: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)" },
              transition: "margin-left 0.3s ease",
              ...(isSmallScreen ? {} : { ml: drawerOpen ? "180px" : "40px" }),
            }}
          >
            <MenuIcon sx={{ color: appBarColors.iconColor, fontSize: "1.1rem" }} />
          </IconButton>

          {/* Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center", mx: 0.5, borderColor: appBarColors.border }} />

          {/* Role Badge */}
          {currentGroup?.id && (
            <Chip
              size="small"
              icon={currentGroup?.isContributor2Group ? <EditIcon /> : <VisibilityIcon />}
              label={currentGroup?.isContributor2Group ? "Editor" : "Viewer"}
              sx={{
                background: currentGroup?.isContributor2Group
                  ? (isDarkMode ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.15)")
                  : appBarColors.chipBg,
                color: currentGroup?.isContributor2Group
                  ? (isDarkMode ? "#4ade80" : "#16a34a")
                  : appBarColors.textSecondary,
                fontWeight: 600,
                fontSize: "0.7rem",
                height: 22,
                "& .MuiChip-icon": {
                  color: currentGroup?.isContributor2Group
                    ? (isDarkMode ? "#4ade80" : "#16a34a")
                    : appBarColors.textSecondary,
                  fontSize: "0.8rem",
                },
              }}
            />
          )}

          {/* Project Selector */}
          <ProjectSelector {...{ onOpen, modalData, currentGroup, isDarkMode }} />
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Dark Mode Toggle */}
          <IconButton
            onClick={handleThemeToggle}
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              "&:hover": { background: appBarColors.hoverBg },
            }}
          >
            <motion.div
              initial={false}
              animate={{ rotate: isDarkMode ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              style={{ display: "flex" }}
            >
              {isDarkMode ? (
                <LightMode sx={{ color: "#fbbf24", fontSize: "1.1rem" }} />
              ) : (
                <DarkMode sx={{ color: appBarColors.textSecondary, fontSize: "1.1rem" }} />
              )}
            </motion.div>
          </IconButton>

          {/* Trial chip — visible only when the caller's account is
              on the trial tier (billing_enabled=false). Self-fetches
              GET /api/accounts/me; renders null otherwise. */}
          <TrialChip />

          {/* Notifications */}
          <IconButton
            onClick={(evt) => onMenuOpen(evt.currentTarget)}
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              "&:hover": { background: appBarColors.hoverBg },
            }}
          >
            <Badge
              badgeContent={unreadCount}
              sx={{
                "& .MuiBadge-badge": {
                  background: "#ef4444",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.55rem",
                  minWidth: 14,
                  height: 14,
                  top: 2,
                  right: 2,
                },
              }}
            >
              <NotificationsOutlined sx={{ color: appBarColors.textSecondary, fontSize: "1.1rem" }} />
            </Badge>
          </IconButton>

          {/* Divider */}
          <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center", mx: 0.5, borderColor: appBarColors.border }} />

          {/* Avatar */}
          <Box
            component="button"
            onClick={onOpenAvatarPopper}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 0.75,
              py: 0.5,
              borderRadius: "6px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              transition: "background 0.15s",
              "&:hover": { background: appBarColors.hoverBg },
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                background: "#F97316",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            >
              {getInitials(`${currentUser?.givenName} ${currentUser?.familyName}`)}
            </Avatar>
            {!isSmallScreen && (
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ fontWeight: 600, color: appBarColors.textPrimary, fontSize: "0.75rem", lineHeight: 1.2 }}>
                  {currentUser?.givenName}
                </Typography>
                <Typography sx={{ color: appBarColors.textSecondary, fontSize: "0.6rem", lineHeight: 1 }}>
                  {currentUser?.userType || "User"}
                </Typography>
              </Box>
            )}
            <KeyboardArrowDown sx={{ color: appBarColors.textMuted, fontSize: "0.9rem" }} />
          </Box>
        </Box>
      </Box>

      {/* Avatar Dropdown */}
      <Popper
        sx={{ zIndex: 10000 }}
        id={id}
        anchorEl={anchorAvatarEl}
        open={openAvatarPopup}
        placement="bottom-end"
      >
        <AnimatePresence>
          {openAvatarPopup && (
            <ClickAwayListener onClickAway={onCloseAvatarPopper}>
              <Paper
                component={motion.div}
                variants={popperVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                sx={{
                  mt: 1,
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: isDarkMode
                    ? "0 4px 20px rgba(0,0,0,0.4)"
                    : "0 4px 20px rgba(0,0,0,0.12)",
                  border: isDarkMode ? "1px solid #334155" : "1px solid #e2e8f0",
                  background: isDarkMode ? "#1e293b" : "#fff",
                  minWidth: "200px",
                }}
              >
                {/* User Header */}
                <Box sx={{ p: 2, borderBottom: isDarkMode ? "1px solid #334155" : "1px solid #f1f5f9" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDarkMode ? "#f1f5f9" : "#0f172a" }}>
                    {currentUser?.givenName} {currentUser?.familyName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                    {currentUser?.email}
                  </Typography>
                </Box>

                {/* Menu */}
                <List sx={{ py: 0.5 }}>
                  {menuItems.map((item, idx) => (
                    <ListItemButton
                      key={idx}
                      onClick={item.onClick}
                      data-testid={item.dataTestId}
                      sx={{
                        py: 1,
                        px: 2,
                        color: isDarkMode ? "#e2e8f0" : "inherit",
                        "&:hover": {
                          background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                        },
                        ...(item.danger && {
                          color: isDarkMode ? "#f87171" : "#dc2626",
                          "&:hover": { background: isDarkMode ? "rgba(239, 68, 68, 0.15)" : "#fef2f2" },
                        }),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </ClickAwayListener>
          )}
        </AnimatePresence>
      </Popper>

      {/* Delete Confirmation Modal */}
      <Modal
        sx={{ width: "40em" }}
        open={confirmDelete}
        handleClose={handleCancelDelete}
        title={`Deleting ${projectToDelete?.name}`}
        saveLabel={t.destroy}
        onSave={handleConfirmDelete}
        specificDetails={{ isSubmitting: modalData?.isSubmitting }}
        closeLabel={t.cancel}
        content={
          <Box>
            <Typography>
              Deleting <strong>{projectToDelete?.name}</strong> will also delete
              everything that was created under this project. Are you sure you
              want to continue?
            </Typography>
          </Box>
        }
      />
    </>
  );
};

export default withErrorHandling(AppBarMain);
